/**
 * DB/스냅샷 JSON의 { kor, eng } 필드를 현재 locale 문자열로 펼쳐 Footer 런타임 구조에 맞춤
 * 표시 순서: sortOrder(권장) → order → sectionIndex/itemIndex → index (오름차순, 생략 시 배열 원래 순서)
 */

/**
 * @param {object} row
 * @param {string[]} keys - 우선순위대로 조회할 필드명
 * @param {number} fallbackIndex - 키가 없을 때 안정 정렬용 (원래 배열 인덱스 + 큰 베이스)
 */
export function numericDisplayOrder(row, keys = ['sortOrder', 'order', 'index'], fallbackIndex = 0) {
  if (row == null || typeof row !== 'object') return 100000 + fallbackIndex;
  for (const k of keys) {
    const v = row[k];
    if (v != null && v !== '') {
      const n = Number(v);
      if (!Number.isNaN(n)) return n;
    }
  }
  return 100000 + fallbackIndex;
}

/**
 * @param {Array} entries
 * @param {string[]} orderKeys
 */
export function sortByDisplayOrder(entries, orderKeys) {
  if (!Array.isArray(entries)) return entries;
  return [...entries]
    .map((row, i) => ({ row, i, ord: numericDisplayOrder(row, orderKeys, i) }))
    .sort((a, b) => (a.ord !== b.ord ? a.ord - b.ord : a.i - b.i))
    .map(({ row }) => row);
}

const LINK_SECTION_ORDER_KEYS = ['sortOrder', 'order', 'sectionIndex', 'index'];
const LINK_ITEM_ORDER_KEYS = ['sortOrder', 'order', 'itemIndex', 'index'];
const COPYRIGHT_LINK_ORDER_KEYS = ['sortOrder', 'order', 'index'];
const CONTACT_ITEM_ORDER_KEYS = ['sortOrder', 'order', 'index'];
const BADGE_ORDER_KEYS = ['sortOrder', 'order', 'index'];
const PARTNER_LOGO_ORDER_KEYS = ['sortOrder', 'order', 'index'];

/**
 * 푸터 협력 로고 <img src> — 입력 중 부분 경로(`/p`, `public` 등)로 GET 요청이 나가
 * dev 서버에서 500·404가 쌓이는 것을 막기 위해, "완성에 가까운" URL/경로일 때만 true.
 */
export function shouldLoadFooterPartnerLogoImageUrl(raw) {
  const u = String(raw ?? '').trim();
  if (u.length < 2) return false;
  if (/^https?:\/\//i.test(u)) {
    return /^https?:\/\/[^\s/]+(\/|$)/.test(u) && u.length >= 9;
  }
  if (u.startsWith('//')) {
    return u.length >= 6 && /^\/\/[^/]+(\/|$)/.test(u);
  }
  if (u.startsWith('/')) {
    const parts = u.split('/').filter(Boolean);
    if (parts.length >= 2) return true;
    if (parts.length === 1 && /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(parts[0])) return true;
    return false;
  }
  return false;
}

export function pickLocaleString(value, locale = 'kor') {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && (Object.prototype.hasOwnProperty.call(value, 'kor') || Object.prototype.hasOwnProperty.call(value, 'eng'))) {
    const v = value[locale] ?? value.kor ?? value.eng;
    return v != null ? String(v) : '';
  }
  return String(value);
}

/** { kor, eng } 객체 또는 primary + *En 평면 필드에서 locale 문자열 선택 */
export function pickLocaleWithEnField(primary, enField, locale = 'kor') {
  if (primary != null && typeof primary === 'object') {
    return pickLocaleString(primary, locale);
  }
  const kor = primary != null ? String(primary) : '';
  const eng = enField != null && String(enField).trim() !== '' ? String(enField) : '';
  if (locale === 'eng' && eng) return eng;
  return kor || eng;
}

function normalizeStatistics(rawStats, locale) {
  if (!Array.isArray(rawStats)) return null;
  const sorted = sortByDisplayOrder(rawStats, ['sortOrder', 'order', 'index']);
  return sorted.map((row) => ({
    ...row,
    label: pickLocaleString(row.label, locale),
    labelEn: typeof row.labelEn === 'string' ? row.labelEn : pickLocaleString(row.label, 'eng')
  }));
}

function normalizeGlowPlatform(gp, locale) {
  if (!gp || typeof gp !== 'object') return null;
  const badges = Array.isArray(gp.badges) ? sortByDisplayOrder(gp.badges, BADGE_ORDER_KEYS) : gp.badges;
  const partnerLogos = Array.isArray(gp.partnerLogos) ? sortByDisplayOrder(gp.partnerLogos, PARTNER_LOGO_ORDER_KEYS) : gp.partnerLogos;
  return {
    ...gp,
    title: pickLocaleWithEnField(gp.title, gp.titleEn, locale),
    subtitle: pickLocaleWithEnField(gp.subtitle, gp.subtitleEn, locale),
    description: pickLocaleWithEnField(gp.description, gp.descriptionEn, locale),
    badges: Array.isArray(badges)
      ? badges.map((b) => ({
          ...b,
          text: pickLocaleWithEnField(b.text, b.textEn, locale)
        }))
      : badges,
    partnerLogos: Array.isArray(partnerLogos)
      ? partnerLogos.map((p) => ({
          ...p,
          alt: pickLocaleWithEnField(p.alt, p.altEn, locale)
        }))
      : partnerLogos
  };
}

function normalizeLinkSections(sections, locale) {
  if (!Array.isArray(sections)) return null;
  const sortedSections = sortByDisplayOrder(sections, LINK_SECTION_ORDER_KEYS);
  return sortedSections.map((sec) => {
    const rawItems = sec.items || [];
    const sortedItems = sortByDisplayOrder(rawItems, LINK_ITEM_ORDER_KEYS);
    const items = sortedItems.map((it) => ({
      ...it,
      text: pickLocaleWithEnField(it.text, it.textEn, locale)
    }));
    return {
      ...sec,
      title: pickLocaleWithEnField(sec.title, sec.titleEn, locale),
      items
    };
  });
}

function normalizeContactBlock(contact, locale) {
  if (!contact || typeof contact !== 'object') return null;
  const sortedItems = sortByDisplayOrder(contact.items || [], CONTACT_ITEM_ORDER_KEYS);
  return {
    ...contact,
    title: pickLocaleWithEnField(contact.title, contact.titleEn, locale),
    items: sortedItems.map((it) => {
      const next = { ...it };
      if (it.title != null) next.title = pickLocaleWithEnField(it.title, it.titleEn, locale);
      next.content = pickLocaleWithEnField(it.content, it.contentEn, locale);
      return next;
    })
  };
}

/**
 * copyright.links: { label: { kor, eng }, link } → FooterBottom용 { kor, eng, link }
 */
export function flattenCopyrightLinksForFooterBottom(copyright) {
  if (!copyright || typeof copyright !== 'object') return copyright;
  const links = copyright.links;
  if (!Array.isArray(links)) return copyright;
  const flat = links.map((item) => {
    if (item && item.label != null && typeof item.label === 'object') {
      return {
        kor: item.label.kor != null ? String(item.label.kor) : '',
        eng: item.label.eng != null ? String(item.label.eng) : '',
        link: item.link
      };
    }
    return item;
  });
  return { ...copyright, links: flat };
}

/**
 * DB 스냅샷의 { kor, eng } 객체를 FooterAreaManager 편집기/미리보기가 기대하는
 * title + titleEn 등 평면 문자열 필드로 바꿈 (React "object as child" 방지).
 */
function splitLocalePair(val) {
  if (val == null) return { kor: '', eng: '' };
  if (typeof val === 'string') return { kor: val, eng: '' };
  if (
    typeof val === 'object' &&
    (Object.prototype.hasOwnProperty.call(val, 'kor') || Object.prototype.hasOwnProperty.call(val, 'eng'))
  ) {
    return {
      kor: val.kor != null ? String(val.kor) : '',
      eng: val.eng != null ? String(val.eng) : ''
    };
  }
  return { kor: String(val), eng: '' };
}

function mergeEnField(existingEn, fromPairEng) {
  if (existingEn != null && String(existingEn).trim() !== '') return String(existingEn);
  return fromPairEng;
}

function flattenCopyrightLinkForEditor(item) {
  if (!item || typeof item !== 'object') return item;
  if (item.label != null && typeof item.label === 'object') {
    return {
      sortOrder: item.sortOrder,
      order: item.order,
      index: item.index,
      kor: item.label.kor != null ? String(item.label.kor) : '',
      eng: item.label.eng != null ? String(item.label.eng) : '',
      link: item.link ?? ''
    };
  }
  const pk = splitLocalePair(item.kor);
  const pe = splitLocalePair(item.eng);
  const korOut = pk.kor || (typeof item.kor === 'string' ? item.kor : '');
  const engOut =
    item.eng == null || item.eng === ''
      ? mergeEnField(item.eng, pk.eng)
      : typeof item.eng === 'string'
        ? item.eng
        : pe.eng || pe.kor;
  const { label: _lb, ...rest } = item;
  return { ...rest, kor: korOut, eng: engOut };
}

export function flattenFooterDataForAreaManagerEditor(footerData) {
  if (!footerData || typeof footerData !== 'object') return footerData;
  const out = JSON.parse(JSON.stringify(footerData));

  if (out.glowPlatform && typeof out.glowPlatform === 'object') {
    const gp = out.glowPlatform;
    const tTitle = splitLocalePair(gp.title);
    gp.title = tTitle.kor;
    gp.titleEn = mergeEnField(gp.titleEn, tTitle.eng);
    const tSub = splitLocalePair(gp.subtitle);
    gp.subtitle = tSub.kor;
    gp.subtitleEn = mergeEnField(gp.subtitleEn, tSub.eng);
    const tDesc = splitLocalePair(gp.description);
    gp.description = tDesc.kor;
    gp.descriptionEn = mergeEnField(gp.descriptionEn, tDesc.eng);
    if (Array.isArray(gp.badges)) {
      gp.badges = sortByDisplayOrder(gp.badges, BADGE_ORDER_KEYS).map((b) => {
        const tx = splitLocalePair(b.text);
        return {
          ...b,
          text: tx.kor,
          textEn: mergeEnField(b.textEn, tx.eng)
        };
      });
    }
    if (Array.isArray(gp.partnerLogos)) {
      gp.partnerLogos = sortByDisplayOrder(gp.partnerLogos, PARTNER_LOGO_ORDER_KEYS).map((row) => {
        const tx = splitLocalePair(row.alt);
        return {
          ...row,
          alt: tx.kor,
          altEn: mergeEnField(row.altEn, tx.eng)
        };
      });
    }
  }

  if (Array.isArray(out.linkSections)) {
    out.linkSections = sortByDisplayOrder(out.linkSections, LINK_SECTION_ORDER_KEYS).map((sec) => {
      const st = splitLocalePair(sec.title);
      const rawItems = sec.items || [];
      const sortedItems = sortByDisplayOrder(rawItems, LINK_ITEM_ORDER_KEYS);
      return {
        ...sec,
        title: st.kor,
        titleEn: mergeEnField(sec.titleEn, st.eng),
        items: sortedItems.map((it) => {
          const tx = splitLocalePair(it.text);
          return {
            ...it,
            text: tx.kor,
            textEn: mergeEnField(it.textEn, tx.eng)
          };
        })
      };
    });
  }

  if (out.contact && typeof out.contact === 'object') {
    const ct = out.contact;
    const tCt = splitLocalePair(ct.title);
    ct.title = tCt.kor;
    ct.titleEn = mergeEnField(ct.titleEn, tCt.eng);
    if (Array.isArray(ct.items)) {
      ct.items = sortByDisplayOrder(ct.items, CONTACT_ITEM_ORDER_KEYS).map((it) => {
        const titlePair = it.title != null ? splitLocalePair(it.title) : { kor: '', eng: '' };
        const contentPair = splitLocalePair(it.content);
        return {
          ...it,
          title: titlePair.kor,
          titleEn: mergeEnField(it.titleEn, titlePair.eng),
          content: contentPair.kor,
          contentEn: mergeEnField(it.contentEn, contentPair.eng)
        };
      });
    }
  }

  if (out.copyright && typeof out.copyright === 'object') {
    const cr = out.copyright;
    if (cr.copyright && typeof cr.copyright === 'object' && !Array.isArray(cr.copyright)) {
      const c = cr.copyright;
      cr.copyright = {
        kor: typeof c.kor === 'object' ? pickLocaleString(c.kor, 'kor') : c.kor != null ? String(c.kor) : '',
        eng: typeof c.eng === 'object' ? pickLocaleString(c.eng, 'eng') : c.eng != null ? String(c.eng) : ''
      };
    }
    if (Array.isArray(cr.links)) {
      cr.links = sortByDisplayOrder(cr.links, COPYRIGHT_LINK_ORDER_KEYS).map((item) => flattenCopyrightLinkForEditor(item));
    }
  }

  return out;
}

/**
 * API에서 locale 펼친 footerData를 코드 기본값과 병합 (누락 필드는 기본 유지)
 */
export function mergeFooterDataWithDefaults(defaultFd, normalizedApi) {
  if (!normalizedApi || typeof normalizedApi !== 'object') return defaultFd;
  return {
    statistics:
      normalizedApi.statistics && normalizedApi.statistics.length > 0 ? normalizedApi.statistics : defaultFd.statistics,
    glowPlatform: normalizedApi.glowPlatform
      ? {
          ...defaultFd.glowPlatform,
          ...normalizedApi.glowPlatform,
          badges: normalizedApi.glowPlatform.badges ?? defaultFd.glowPlatform.badges,
          partnerLogos: normalizedApi.glowPlatform.partnerLogos ?? defaultFd.glowPlatform.partnerLogos
        }
      : defaultFd.glowPlatform,
    linkSections:
      normalizedApi.linkSections && normalizedApi.linkSections.length > 0 ? normalizedApi.linkSections : defaultFd.linkSections,
    contact: normalizedApi.contact ? normalizedApi.contact : defaultFd.contact,
    copyright: normalizedApi.copyright ?? defaultFd.copyright
  };
}

/**
 * @param {object} rawFooterData — footerContent.footerData 원본
 * @param {string} locale — 'kor' | 'eng'
 * @returns {object|null} locale 펼친 footerData (아이콘 미부착)
 */
export function normalizeFooterDataForRuntime(rawFooterData, locale = 'kor') {
  if (!rawFooterData || typeof rawFooterData !== 'object') return null;
  const out = { ...rawFooterData };
  if (rawFooterData.glowPlatform) {
    out.glowPlatform = normalizeGlowPlatform(rawFooterData.glowPlatform, locale);
  }
  if (rawFooterData.linkSections) {
    out.linkSections = normalizeLinkSections(rawFooterData.linkSections, locale);
  }
  if (rawFooterData.contact) {
    out.contact = normalizeContactBlock(rawFooterData.contact, locale);
  }
  if (rawFooterData.statistics) {
    out.statistics = normalizeStatistics(rawFooterData.statistics, locale);
  }
  if (rawFooterData.copyright) {
    const cp = { ...rawFooterData.copyright };
    if (Array.isArray(cp.links)) {
      cp.links = sortByDisplayOrder(cp.links, COPYRIGHT_LINK_ORDER_KEYS);
    }
    out.copyright = flattenCopyrightLinksForFooterBottom(cp);
  }
  return out;
}
